/**
 * Auth types and pure helpers.
 *
 * This file has no React and no side effects. The mutation lives in
 * auth-store.ts; when the real API arrives, only that file swaps —
 * types and validators here stay as the contract.
 */

export type UserId = string & { __brand: "UserId" }

// The Role union lives in `lib/roles` so route guards, nav, and API clients share one policy.
// Re-exported here so existing `import { Role } from "@/features/auth"` sites keep working.
export type { Role } from "@/lib/roles"

export interface LoginInput {
  email: string
  password: string
  /** When true, backend issues a 30-day refresh token and the session
   *  is stored in localStorage. When false, tab-lifetime only. */
  rememberMe: boolean
}

export interface User {
  id: UserId
  email: string
  name: string | null
  role: Role
}

export interface Session {
  user: User
  /** ISO string; renew before this. */
  expiresAt: string
}

/**
 * Discriminated union so callers can render specific messages without
 * string-matching. Extend this enum as the API defines new failure
 * modes — never fall back to a generic string when a code exists.
 */
export type LoginErrorKind =
  | "invalid_credentials"
  | "rate_limited"
  | "account_locked"
  | "network"
  | "server"
  | "unknown"

export class LoginError extends Error {
  readonly kind: LoginErrorKind
  /** Seconds until the user may retry, when the API provides one. */
  readonly retryAfterSeconds: number | null

  constructor(kind: LoginErrorKind, message: string, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = "LoginError"
    this.kind = kind
    this.retryAfterSeconds = retryAfterSeconds
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim())
}

export interface FieldErrors {
  email: string | null
  password: string | null
}

export function validateLoginInput(input: Pick<LoginInput, "email" | "password">): FieldErrors {
  const email = input.email.trim()
  return {
    email:
      email.length === 0
        ? "Enter your email."
        : !isValidEmail(email)
          ? "Enter a valid email address."
          : null,
    password: input.password.length === 0 ? "Enter your password." : null,
  }
}

export function hasFieldError(errors: FieldErrors): boolean {
  return errors.email !== null || errors.password !== null
}
